const RMF_INCOME_RATE = 0.3;
const RMF_INDIVIDUAL_CAP = 500000;
const PVD_INCOME_RATE = 0.15;
const PVD_INDIVIDUAL_CAP = 500000;
const THAI_ESG_INCOME_RATE = 0.3;
const THAI_ESG_INDIVIDUAL_CAP = 300000;

// รับเฉพาะจำนวนเงินชนิด number ที่เป็น finite และไม่ติดลบ เพื่อไม่ให้ข้อมูลผิดรูปแบบเพิ่มสิทธิ์ลดหย่อน
const normalizeNonNegativeAmount = (value) => {
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

// อ่านยอดรายได้รวม Premium ที่ Step 1 คำนวณจากข้อมูลภาษีและ Balance ไว้แล้ว โดยไม่ย้อนกลับไปใช้ช่องเงินเดือนที่อาจว่าง
export const resolvePremiumTaxPlanAnnualIncome = (taxData) => {
  return normalizeNonNegativeAmount(taxData?.tax_sum_income);
};

// เลือกฐานรายได้สำหรับค่าลดหย่อนตามประเภทผู้ใช้ เพื่อให้ PVD, Thai ESG และกองทุนที่เกี่ยวข้องใช้ authority เดียวกัน
export const resolveTaxDeductionAnnualIncome = ({
  isPremium,
  normalAnnualIncome,
  premiumAnnualIncome,
}) => {
  const selectedAnnualIncome = isPremium
    ? premiumAnnualIncome
    : normalAnnualIncome;

  return normalizeNonNegativeAmount(selectedAnnualIncome);
};

// สร้าง payload object จากรายได้ใน Balance โดยคง field ภาษีเดิมไว้ เพราะ Zustand store นี้ไม่รองรับ functional updater
export const createBalanceTaxIncomeUpdate = (taxData, annualIncome) => {
  return {
    ...(taxData || {}),
    tax_income: annualIncome,
  };
};

// คำนวณเพดาน คงเหลือ progress และผลตรวจ PVD จากรายได้ authority ชุดเดียวกัน เพื่อไม่ให้ค่าที่แสดงแยกจาก validation
export const calculatePvdExistingContributionEntitlement = ({
  isPremium,
  normalAnnualIncome,
  premiumAnnualIncome,
  existingPvd,
}) => {
  const resolvedAnnualIncome = resolveTaxDeductionAnnualIncome({
    isPremium,
    normalAnnualIncome,
    premiumAnnualIncome,
  });
  const normalizedExistingPvd = normalizeNonNegativeAmount(existingPvd);
  const maximumAllowance = Math.min(
    resolvedAnnualIncome * PVD_INCOME_RATE,
    PVD_INDIVIDUAL_CAP
  );

  return {
    resolvedAnnualIncome,
    maximumAllowance,
    existingPvd: normalizedExistingPvd,
    remainingAllowance: Math.max(maximumAllowance - normalizedExistingPvd, 0),
    usagePercent:
      maximumAllowance === 0
        ? 0
        : (normalizedExistingPvd / maximumAllowance) * 100,
    isWithinAllowance: normalizedExistingPvd <= maximumAllowance,
  };
};

// คำนวณเพดาน ยอดคงเหลือ progress และผล validation ของ RMF เดิมจาก entitlement ชุดเดียวกัน
export const calculateRmfExistingPurchaseEntitlement = ({
  isPremium,
  normalAnnualIncome,
  premiumAnnualIncome,
  existingRmf,
}) => {
  const resolvedAnnualIncome = resolveTaxDeductionAnnualIncome({
    isPremium,
    normalAnnualIncome,
    premiumAnnualIncome,
  });
  const normalizedExistingRmf = normalizeNonNegativeAmount(existingRmf);
  const maximumAllowance = Math.min(
    resolvedAnnualIncome * RMF_INCOME_RATE,
    RMF_INDIVIDUAL_CAP
  );

  return {
    resolvedAnnualIncome,
    maximumAllowance,
    existingRmf: normalizedExistingRmf,
    remainingAllowance: Math.max(maximumAllowance - normalizedExistingRmf, 0),
    usagePercent:
      maximumAllowance === 0
        ? 0
        : (normalizedExistingRmf / maximumAllowance) * 100,
    isWithinAllowance: normalizedExistingRmf <= maximumAllowance,
  };
};

// คำนวณสิทธิ์ซื้อ RMF ที่เหลือ โดยหักยอด RMF เดิมจากเพดานรายบุคคลเพียงครั้งเดียว
export const calculateRmfRemainingAllowance = ({
  annualIncome,
  existingRmf,
  combinedRemainingAllowance,
}) => {
  const normalizedIncome = normalizeNonNegativeAmount(annualIncome);
  const normalizedExistingRmf = normalizeNonNegativeAmount(existingRmf);
  const normalizedCombinedRemaining = normalizeNonNegativeAmount(
    combinedRemainingAllowance
  );
  const incomeBasedAllowance = Math.min(
    normalizedIncome * RMF_INCOME_RATE,
    RMF_INDIVIDUAL_CAP
  );
  const individualRemaining = Math.max(
    incomeBasedAllowance - normalizedExistingRmf,
    0
  );

  return Math.min(individualRemaining, normalizedCombinedRemaining);
};

// สิทธิ์คงเหลือหักยอดเดิมแล้ว จึงตรวจเฉพาะยอดซื้อใหม่และยอมรับค่าที่เท่ากับเพดานพอดี
export const isRmfPlannedPurchaseWithinAllowance = (
  plannedPurchase,
  remainingAllowance
) => {
  return (
    Number.isFinite(plannedPurchase) &&
    plannedPurchase >= 0 &&
    Number.isFinite(remainingAllowance) &&
    remainingAllowance >= 0 &&
    plannedPurchase <= remainingAllowance
  );
};

// เลือกรายได้ตามสิทธิ์ผู้ใช้เพียงครั้งเดียว แล้วคำนวณเพดานและสิทธิ์ Thai ESG คงเหลือจากรายได้ชุดเดียวกัน
export const calculateThaiEsgEntitlement = ({
  isPremium,
  normalAnnualIncome,
  premiumAnnualIncome,
  existingThaiEsg,
}) => {
  const resolvedAnnualIncome = resolveTaxDeductionAnnualIncome({
    isPremium,
    normalAnnualIncome,
    premiumAnnualIncome,
  });
  const normalizedExistingThaiEsg = normalizeNonNegativeAmount(existingThaiEsg);
  const maximumAllowance = Math.min(
    resolvedAnnualIncome * THAI_ESG_INCOME_RATE,
    THAI_ESG_INDIVIDUAL_CAP
  );

  return {
    resolvedAnnualIncome,
    maximumAllowance,
    existingThaiEsg: normalizedExistingThaiEsg,
    remainingAllowance: Math.max(
      maximumAllowance - normalizedExistingThaiEsg,
      0
    ),
  };
};

// ตรวจยอดซื้อใหม่รวมยอดเดิมกับเพดานจาก entitlement ชุดเดียวกับที่ UI แสดง โดยยอมรับยอดที่เท่ากับเพดานพอดี
export const isThaiEsgPlannedPurchaseWithinAllowance = (
  plannedPurchase,
  entitlement
) => {
  const maximumAllowance = entitlement?.maximumAllowance;
  const existingThaiEsg = entitlement?.existingThaiEsg;

  return (
    Number.isFinite(plannedPurchase) &&
    plannedPurchase >= 0 &&
    Number.isFinite(maximumAllowance) &&
    maximumAllowance >= 0 &&
    Number.isFinite(existingThaiEsg) &&
    existingThaiEsg >= 0 &&
    plannedPurchase + existingThaiEsg <= maximumAllowance
  );
};
